// # CODE FOR ZOSA - Current Date 2026-01-03
// ------ API Functions -----

import { getAuth } from "firebase/auth";
import { db } from './firebase-config';
import { doc, updateDoc } from 'firebase/firestore';
// Assuming logErrorToFirestore is exported from firebaseService
import { logErrorToFirestore } from "./firebaseService";

// Define the API base URL dynamically based on the environment
const API_BASE_URL =
    process.env.NODE_ENV === "development"
        ? `http://${window.location.hostname}:8000`// Your local backend URL
        : "https://zosa-a7ff146bf62b.herokuapp.com"; // Your production backend URL

// --- Helper Functions ---

/**
 * Gets the current user's Firebase ID token.
 * Returns the token string or null if not logged in or error occurs.
 */
const getIdToken = async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
        try {
            return await user.getIdToken(true);
        } catch (error) {
            //console.error("Error getting ID token:", error);
            return null;
        }
    }
    return null; // No user logged in
};

/**
 * Centralized error logging function.
 * Logs to console, Firestore (via firebaseService), and backend endpoint.
 * @param {object} errorDetails - Contextual information about the error.
 * @param {Error | any} [errorObject] - The actual Error object, if available.
 */
const logError = async (errorDetails, errorObject = null) => {
    try {
        const auth = getAuth();
        const detailedError = {
            user_id: auth.currentUser?.uid || errorDetails.user_id || "Unknown/Anonymous",
            session_id: errorDetails.session_id || localStorage.getItem('sessionId_fallback') || "Unknown",
            error_message: errorObject?.message || errorDetails.error_message || "Unknown error",
            stack_trace: errorObject?.stack || errorDetails.stack_trace || "No stack trace available",
            // stack_trace: "Stack trace not logged for security reasons",
            context: errorDetails.context || "No context provided",
            url: window.location.href || "Unknown",
            user_agent: navigator.userAgent || "Unknown",
        };

        await logErrorToFirestore(detailedError);

        const ignoredErrors = [
            "Failed to generate authentication token",
            "auth/network-request-failed"
        ];

        const shouldSkipEmail = ignoredErrors.some(ignored => 
            detailedError.error_message.includes(ignored)
        );

        if (shouldSkipEmail) {
            return;
        }

        await fetch(`${API_BASE_URL}/api/log-error`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(detailedError),
            keepalive: true
        });

    } catch (err) {
        //console.error("!!! Failed to log error itself:", err);
    }
};



export const logErrorExternal = async (errorDetails, errorObject = null) => {
    try {
        const auth = getAuth();
        const detailedError = {
            user_id: auth.currentUser?.uid || errorDetails.user_id || "Unknown/Anonymous",
            session_id: errorDetails.session_id || localStorage.getItem('sessionId_fallback') || "Unknown", 
            error_message: errorObject?.message || errorDetails.error_message || "Unknown error",
            stack_trace: errorObject?.stack || errorDetails.stack_trace || "No stack trace available",
            context: errorDetails.context || "No context provided",
            url: window.location.href || "Unknown",
            user_agent: navigator.userAgent || "Unknown",
        };

        // Log directly to Firestore first (more reliable)
        await logErrorToFirestore(detailedError);

        // Then, attempt to log to backend endpoint (best effort)
        await fetch(`${API_BASE_URL}/api/log-error`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(detailedError),
            keepalive: true
        });

    } catch (err) {
        //console.error("!!! Failed to log error itself:", err);
    }
};

/**
 * Wrapper around fetch for common handling like auth, errors, and response parsing.
 * @param {string} url - The API endpoint URL (relative to API_BASE_URL).
 * @param {object} options - Fetch options (method, headers, body, etc.).
 * @param {boolean} [includeAuth=true] - Whether to include the Firebase Auth token.
 * @returns {Promise<any>} - The parsed response.
 * @throws {Error} - Throws an error if the fetch fails or response is not ok.
 */
export const fetchApi = async (url, options = {}, includeAuth = true, retries = 1) => {
    const headers = {
        "Content-Type": "application/json",
        ...options.headers,
    };

    const { rawStream = false, expectedErrors = [], responseType = 'json', suppressLogging = false } = options;

    // Add auth token
    if (includeAuth) {
        const auth = getAuth();
        let user = auth.currentUser;

        if (!user) {
            user = await new Promise((resolve) => {
                const unsubscribe = auth.onAuthStateChanged((user) => {
                    unsubscribe();
                    resolve(user);
                });
            });
        }

        if (user) {
            try {
                const token = await user.getIdToken(); 
                headers["Authorization"] = `Bearer ${token}`;
            } catch (error) {
                const authError = new Error(`Failed to generate authentication token for ${url}. The user session may be invalid.`);
                authError.response = { status: 401 }; 
                if (error.code !== 'auth/network-request-failed') {
                    await logError({
                        context: `ClientAuthTokenGeneration fetchApi ${options.method || 'GET'} ${url}`,
                        error_message: error.message,
                    }, error);
                }
                throw authError;
            }
        } else {
            // If there's still no user after waiting, they are genuinely not logged in.
            const authError = new Error(`Authentication token is required for ${url} but is not available.`);
            authError.response = { status: 401 }; 
            await logError({
                context: `ClientAuth fetchApi ${options.method || 'GET'} ${url}`,
                error_message: authError.message,
            }, authError);
            throw authError;
        }
    }


    try {
        const response = await fetch(`${API_BASE_URL}${url}`, {
            ...options,
            headers,
        });

        if (!response.ok) {
            let payload = null;
            try {
              payload = await response.clone().json();
            } catch (_) {
              /* ignore – response wasn’t JSON */
            }
          
            let humanMsg = response.statusText;
          
            if (payload?.detail) {
              if (typeof payload.detail === "string") {
                humanMsg = payload.detail;
              } else if (
                typeof payload.detail === "object" &&
                typeof payload.detail.message === "string"
              ) {
                humanMsg = payload.detail.message;
              } else {
                humanMsg = JSON.stringify(payload.detail);
              }
            }
          
            const err = new Error(humanMsg);
            err.response = { status: response.status, data: payload };
            throw err;
          }
         
        if (response.status === 204) {
             return null;
        }

        if (rawStream) return response;

        // Handle different response types based on the new option
        if (responseType === 'blob') {
            return response.blob();
        }

        // Default to JSON
        return response.json();

    } catch (error) {

        const isNetworkError = error.message === "Failed to fetch" || error.message.includes("NetworkError");
        
        if (isNetworkError && retries > 0) {
            // console.log(`Network glitch on ${url}, retrying...`);
            await new Promise(res => setTimeout(res, 1000)); // Wait 1 second
            return fetchApi(url, options, includeAuth, retries - 1); // Retry recursively
        }


        if (!suppressLogging && !expectedErrors.includes(error.response?.status)) {
            await logError({
                context: `WorkspaceApi ${options.method || 'GET'} ${url}`,
                error_message: error.message,
            }, error);
        }
        throw error;
    }
};


const fetchPublicApi = async (url, options = {}) => {
    const headers = {
        "Content-Type": "application/json",
        ...options.headers,
    };

    const token = await getIdToken();
    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }

    try {
        const response = await fetch(`${API_BASE_URL}${url}`, {
            ...options,
            headers,
        });

        if (!response.ok) {
            let payload = null;
            try {
              payload = await response.clone().json();
            } catch (_) {
              /* Response wasn't JSON */
            }
            
            let humanMsg = response.statusText;
            if (payload?.detail) {
               humanMsg = typeof payload.detail === 'string' ? payload.detail : JSON.stringify(payload.detail);
            }
          
            const err = new Error(humanMsg);
            err.response = { status: response.status, data: payload };
            throw err;
        }
         
        if (response.status === 204) {
             return null;
        }

        // Using options to determine response type, defaults to json
        const { rawStream = false, responseType = 'json' } = options;
        if (rawStream) return response;
        if (responseType === 'blob') return response.blob();
        return response.json();

    } catch (error) {
        // We can simplify error logging here as it's for public data
        // console.error(`Public API Error for ${url}:`, error);
        throw error;
    }
};

// --- NEW Session Management Functions ---

/**
 * Creates a new user session on the backend.
 * Requires authentication (anonymous or real user).
 * @param {object} sessionData - { name, initialAnswer?, mood? }
 * @returns {Promise<{session_id: string}>}
 */
export const createSession = async (sessionData) => {
    localStorage.removeItem('taskState');
    return fetchApi("/api/sessions", {
        method: "POST",
        body: JSON.stringify(sessionData),
        expectedErrors: [503],
    }, true); // Auth token is required
};


export const continueSession = async (sessionId) => {
    return fetchApi(
      `/api/sessions/${sessionId}/resume`,
      { method: "PATCH" },
      true
    );
  }

/**
 * Fetches the current state of a session from the backend.
 * @param {string} sessionId
 * @returns {Promise<{session_id: string, user_name: string, messages: Array, step: string, user_id: string|null}>}
 */
export const getSessionState = async (
  sessionId,
  { limit = 50, before } = {},
  includeAuth = true
) => {
  if (!sessionId) {
    throw new Error("Session ID is required to get session state.");
  }

  // build query-string
  const qs = new URLSearchParams({ limit: String(limit) });
  if (before) qs.append("before", before);

  return fetchApi(
    `/api/sessions/${encodeURIComponent(sessionId)}?${qs.toString()}`,
    {},
    includeAuth
  );
};

/**
 * Convenience helper: loads the next-oldest page (defaults to 30 messages)
 */
export const fetchOlderMessages = (sessionId, cursor, limit = 30) =>
  getSessionState(sessionId, { before: cursor, limit });

/**
 * Ends a specific session on the backend.
 * @param {string} sessionId
 * @returns {Promise<{message: string}>}
 */
// api.ts ---------------------------------------------------------
export const endSession = async (sessionId, onChunk, onError, onComplete) => {
    const token = await getIdToken();
    if (!token) {
        onError(new Error("Authentication required."));
        return;
    }

    try {
        const response = await fetch(`${API_BASE_URL}/api/sessions/${sessionId}/end`, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
            },
        });

        if (!response.ok || !response.body) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                onComplete();
                break;
            }
            onChunk(decoder.decode(value, { stream: true }));
        }
    } catch (error) {
        // console.error("Error ending session:", error);
        onError(error);
    }
};

/**
 * Ends a session without generating the written summary.
 *
 * Still calls /end: the post-session safety check, long-term memory, symptom
 * graph and style profile are all queued there. Only the summary is skipped.
 * @param {string} sessionId
 */
export const endSessionWithoutSummary = async (sessionId) =>
    fetchApi(`/api/sessions/${sessionId}/end?summary=false`, { method: 'POST' }, true);


/**
 * Stream a “Post Session Actions” message from the back‑end and pass it to
 * the UI chunk‑by‑chunk (just like normal chat streaming).
 *
 * @param {string}   sessionId  – Firestore/DB session id
 * @param {Function} onChunk    – called with each decoded text chunk
 * @param {Function} onError    – called once if the stream errors
 * @param {Function} onComplete – called once when the stream finishes cleanly
 */
export const generatePostSessionActions = async (sessionId, fullHistory, onChunk, onError, onComplete) => {
  
    const actionsPrompt = `<system_instruction>
        You are an expert clinical psychologist. Your goal is to synthesize the preceding conversation into "Post Session Actions".
        
        **STRATEGY - THE MIX:**
        Generate exactly 3 to 5 actions, structured as follows:
        1. **Immediate "Quick Wins" (First 1-2 items):** Concrete, low-friction tasks the user can do *right now* or today.
        2. **Deeper Work (Remaining items):** Ongoing habits, behavioral experiments, or reflective exercises for the week ahead.
        
        **CRITERIA:**
        - **Relevance:** Actions must be directly derived from the specific challenges, insights, or emotions discussed in this session.
        - **Actionable:** Keep them concise (under 15 words).
        
        **STRICT FORMATTING RULES:**
        - Output the exact title: "**Post Session Actions**" on the first line.
        - Followed immediately by the bullet points. 
        - Use a standard hyphen "-" for bullets.
        - **NO** headers separating short/long term (output as a single flat list).
        - **NO** introductory text.
        - **NO** concluding text.
        
        Example Output:
        **Post Session Actions**
        - Drink a glass of water and take 5 deep breaths.
        - Write down one specific fear you conquered today.
        - Practice the 5-4-3-2-1 grounding technique daily this week.
        - Monitor your self-talk for "should" statements.
        </system_instruction>`;

  try {
      // ATTEMPT 1: Use the fast, stateful /respond endpoint.
      await postSessionMessage(
          sessionId,
          actionsPrompt,
          onChunk,
          (error) => { throw error; }, // Re-throw to be caught by the outer catch
          onComplete,
          { ui_hidden: 'true' }
      );
  } catch (error) {
      if (error.response?.status === 400 || error.response?.status === 410 || error.response?.status === 404) {
        //   console.warn("generatePostSessionActions: Stateful context expired. Retrying with stateless method.");

          // ATTEMPT 2: Fallback to the stateless endpoint.
          try {
              const historyForStatelessCall = [
                  ...fullHistory.map(({ role, content }) => ({ role, content })),
                  { role: 'user', content: actionsPrompt }
              ];
              await postStatelessSessionMessage(
                  sessionId,
                  historyForStatelessCall,
                  onChunk,
                  onError,
                  onComplete
              );
          } catch (fallbackError) {
            //   console.error("generatePostSessionActions: Stateless fallback failed.", fallbackError);
              onError(fallbackError);
          }
      } else {
        //   console.error("generatePostSessionActions: An unexpected error occurred.", error);
          onError(error);
      }
  }
};
  
  

  

/**
 * Sends a user message to the session and handles the streaming response.
 * Requires authentication.
 * @param {string} sessionId - The ID of the session.
 * @param {string} messageContent - The user's message text.
 * @param {function(string): void} onChunk - Callback for each received chunk of text.
 * @param {function(Error): void} onError - Callback for errors during the stream.
 * @param {function(): void} onComplete - Callback when the stream finishes successfully.
 */
// Full file: api.js (Relevant function update)

// ... existing imports and helper functions ...

/**
 * Sends a user message to the session and handles the streaming response.
 * Requires authentication.
 * @param {string} sessionId - The ID of the session.
 * @param {string} messageContent - The user's message text.
 * @param {function(string): void} onChunk - Callback for each received chunk of text.
 * @param {function(Error): void} onError - Callback for errors during the stream.
 * @param {function(): void} onComplete - Callback when the stream finishes successfully.
 */
export const postSessionMessage = async (sessionId, messageContent, onChunk, onError, onComplete, metadata = {}) => {
    
    if (!sessionId) {
        onError(new Error("Session ID is required."));
        return;
    }
    if (!messageContent) {
        onError(new Error("Message content cannot be empty."));
        return;
    }

    try {
        // USE the fetchApi wrapper, not native fetch
        const response = await fetchApi(
            `/api/sessions/${sessionId}/respond`,
            {
                method: "POST",
                body: JSON.stringify({
                    content: messageContent,
                    ...(metadata && Object.keys(metadata).length ? { metadata } : {})
                }),
                rawStream: true,
                expectedErrors: [400, 403, 410]
            },
            true, // Tell fetchApi auth is required
            3 // Retry 3 times
        );

        // The rest of your streaming logic is correct
        if (!response.body) {
            throw new Error("Response body is missing.");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = ''; // A buffer to hold partial lines
        let reading = true;

        while (reading) {
            const { done, value } = await reader.read();
            if (done) {
                reading = false;
                // If there's any text left in the buffer, process it
                if (buffer) {
                    onChunk(buffer);
                }
                onComplete();
                break;
            }

            buffer += decoder.decode(value, { stream: true });
            let eolIndex; // End-of-line index

            // Process all complete lines in the buffer
            while ((eolIndex = buffer.indexOf('\n')) >= 0) {
                const line = buffer.substring(0, eolIndex).trim();
                buffer = buffer.substring(eolIndex + 1);

                if (line.startsWith("event: error")) {
                    continue;
                } 
                
                if (line.startsWith("data:")) {
                    const dataStr = line.substring(5).trim();
                    try {
                        const dataJson = JSON.parse(dataStr);
                        if (dataJson.error) {
                            const error = new Error(dataJson.error.message || "Stream error");
                            error.code = dataJson.error.code || "UNKNOWN";
                            
                            if (error.code === "CONTEXT_EXPIRED") {
                                error.response = { status: 410, body: dataJson };
                            } else {
                                error.response = { status: 500, body: dataJson };
                            }
                            throw error;
                        }
                    } catch (e) {
                        if (e.response) throw e; 
                        onChunk(line + '\n'); 
                    }
                } else if (line) {
                    onChunk(line + '\n'); 
                }
            }
        }

    } catch (error) {
        // --- Filter out "noise" errors caused by user interruption ---
        const isInterruption = 
            error.name === 'AbortError' || 
            error.message.includes('The user aborted a request') ||
            error.message.includes('BodyStreamBuffer was aborted') ||
            error.message.includes('Load failed') ||
            error.message === 'Failed to fetch'; 

        // Only log if it's NOT an interruption and NOT a known status code (400/403/410)
        if (!isInterruption && error.response?.status !== 400 && error.response?.status !== 403 && error.response?.status !== 410) {
            await logError({
                context: `postSessionMessage stream (catch)`,
                error_message: error.message,
                session_id: sessionId
            }, error);
        }
        
        onError(error);
    }
};


// --- Existing Independent Functions (Keep as is or adapt context) ---

/**
 * Generates speech audio from text using the backend endpoint.
 * This function now uses the fetchApi wrapper for consistency.
 * @param {string} text - The text to synthesize.
 * @returns {Promise<Blob>} - The audio data as a Blob.
 */
export const generateSpeech = async (text, sessionId) => {
    if (!sessionId) {
        // console.warn("generateSpeech called without a session ID.");
        throw new Error("Session ID is required for speech generation.");
    }
    return fetchApi(
        "/audio/speech",
        {
            method: "POST",
            body: JSON.stringify({ text, session_id: sessionId }),
            responseType: 'blob' // Expect a blob, not JSON
        },
        true // Auth is now required
    );
};

/**
 * Sends contact form data to the backend.
 * @param {object} formData - { name, email, message }
 * @returns {Promise<any>} - Backend response (e.g., { message: "Email sent successfully" })
 */
export const sendContactForm = async (formData) => {
    return fetchApi("/api/contact", {
        method: "POST",
        body: JSON.stringify(formData),
    }, false); // No auth needed for contact form
};

/**
 * Submits user feedback to the backend. Requires authentication.
 * @param {object} feedbackData - { name, satisfaction, feedback, submitted_at }
 * @returns {Promise<any>} - Backend response.
 */
export const submitFeedback = async (feedbackData) => {
    const auth = getAuth();
    const user = auth.currentUser;
    const dataToSend = {
        ...feedbackData,
        user_id: user?.uid || "Anonymous", // Include user_id if available
    };
    return fetchPublicApi("/api/submit-feedback", {
        method: "POST",
        body: JSON.stringify(dataToSend),
    });
};


// --- Reminder Functions (Keep as is) ---

export const sendSingleReminder = async (userId, appointmentId) => {
    return fetchApi("/api/send-reminder", {
        method: "POST",
        body: JSON.stringify({ uid: userId, appointment_id: appointmentId }),
    }, false); 
};

export const sendTestReminder = async (userId, appointmentId) => {
     const testDateTime = new Date().toISOString();
     return fetchApi("/api/test-reminders", { 
          method: "POST",
          body: JSON.stringify({
               uid: userId,
               appointment_id: appointmentId,
               dateTime: testDateTime, 
               getReminder: "Y"
          }),
     }, false); 
};


export const notifyAdminOfAccountDeletion = async (userId, email, reason, hasMembership) => {
    try {
        await fetch(`${API_BASE_URL}/api/account-deleted`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                user_id: userId,
                email: email,
                reason: reason || "No reason provided",
                has_membership: hasMembership,
            }),
        });
    } catch (err) {
        //console.error("Failed to notify admin of deletion:", err);
    }
};


/** Thrown by deleteAccount when the server refuses, so the UI can react to the reason. */
export class DeleteAccountError extends Error {
    constructor(code, message, provider) {
        super(message);
        this.name = "DeleteAccountError";
        this.code = code;
        this.provider = provider;
    }
}

/**
 * Permanently deletes the signed-in account and all of its data.
 *
 * The server does the work. Deleting documents from the client only reached the
 * handful of top-level docs listed in the component and never touched subcollections,
 * so sessions, notes, memories and safety flags outlived the account.
 *
 * Call this AFTER reauthenticating: the token is refreshed so the server sees the new
 * auth_time, and it requires a recent sign-in before it will delete anything.
 */
export const deleteAccount = async (reason) => {
    const user = getAuth().currentUser;
    if (!user) throw new Error("User not authenticated.");

    const token = await user.getIdToken(true);
    const res = await fetch(
        `${API_BASE_URL}/api/users/me?reason=${encodeURIComponent(reason || "")}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } },
    );

    if (res.status === 204) return;

    const data = await res.json().catch(() => ({}));
    const detail = data?.detail;

    if (detail && typeof detail === "object" && detail.code) {
        throw new DeleteAccountError(detail.code, detail.message || "Could not delete account.", detail.provider);
    }
    throw new DeleteAccountError(
        "UNKNOWN",
        typeof detail === "string" ? detail : "Failed to delete account. Please try again.",
    );
};


export const resetAccountData = async () => {
    const user = getAuth().currentUser;
    if (!user) throw new Error("User not authenticated.");
    const token = await user.getIdToken();
    const res = await fetch(`${API_BASE_URL}/api/reset-account`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_id: user.uid }),
    });
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Failed to reset account.");
    }
    // Clear cached session data
    sessionStorage.removeItem("zosaSessionId");
    sessionStorage.removeItem("zosaVoiceSessionId");
};

export const markDeepInsightSent = (uid, sessionId) =>
      updateDoc(
        doc(db, 'user_sessions', uid, 'sessions', sessionId),
        { deep_insight_sent: true }
      );


// --- Deep Analysis Polling Functions ---

/**
 * Initiates the deep analysis process on the backend.
 * @param {string | null} passphrase - The user's passphrase for decryption.
 * @returns {Promise<{task_id: string}>} - An object containing the task ID for polling.
 */
export const startDeepAnalysis = async (passphrase) => {
  const auth = getAuth();
  const currentUser = auth.currentUser;

  if (!currentUser) {
      throw new Error("User not authenticated. Cannot start deep analysis.");
  }

  const payload = {
      user_id: currentUser.uid,
      passphrase: passphrase || null,
  };

  return fetchApi("/api/start-analysis", {
      method: "POST",
      body: JSON.stringify(payload),
  }, true); 
};

/**
* Fetches the current status of an ongoing analysis task.
* @param {string} taskId - The ID of the task returned by startDeepAnalysis.
* @returns {Promise<{status: string, progress_message: string, report_text?: string, error_detail?: string, error_code?: string}>}
*/
export const getAnalysisStatus = async (taskId) => {
  if (!taskId) {
      throw new Error("A task ID is required to get analysis status.");
  }

  return fetchApi(`/api/analysis-status/${taskId}`, {
      method: "GET",
  }, true);
};


export const postStatelessSessionMessage = async (sessionId, fullHistory, onChunk, onError, onComplete) => {
  if (!sessionId) {
      onError(new Error("Session ID is required for stateless fallback."));
      return;
  }
  if (!fullHistory || fullHistory.length === 0) {
      onError(new Error("Conversation history cannot be empty for stateless fallback."));
      return;
  }

  const token = await getIdToken();
  if (!token) {
      onError(new Error("Authentication required to send messages."));
      return;
  }

  try {
      const response = await fetch(`${API_BASE_URL}/api/sessions/${sessionId}/respond_stateless`, {
          method: "POST",
          headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`,
              "Accept": "text/event-stream", // Necessary for streaming
          },
          body: JSON.stringify({
              history: fullHistory,
          }),
      });

      if (!response.ok) {
          let errorBody = null;
          try {
              errorBody = await response.json();
          } catch (e) {
              // The error response wasn't JSON
          }
          const error = new Error(errorBody?.detail || `HTTP error! Status: ${response.status}`);
          error.status = response.status;
          throw error;
      }

      if (!response.body) {
          throw new Error("Response body is missing from stateless call.");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      
      while (true) {
          const { done, value } = await reader.read();
          if (done) {
              onComplete();
              break;
          }
          const chunk = decoder.decode(value, { stream: true });
          onChunk(chunk);
      }

  } catch (error) {
      await logError({
          context: `postStatelessSessionMessage stream`,
          error_message: error.message,
          session_id: sessionId
      }, error);
      onError(error);
  }
};


export const getLastSessionIdFromUser = async () => {
    try {
        const response = await fetchApi(
            "/api/users/me/last-session-id", 
            { expectedErrors: [404] }, 
            true
        );
        return response.zosaSessionID || null;
    } catch (error) {
        return null;
    }
};

export const clearLastSessionIdFromUser = async () => {
    try {
        const auth = getAuth();
        const currentUser = auth.currentUser;

        if (!currentUser || currentUser.isAnonymous) {
            return;
        }
        await fetchApi("/api/users/me/last-session-id", { method: "DELETE" }, true);

    } catch (error) {
        // This catch is a fallback, but the check above should prevent most errors.
        // console.warn("Could not clear last session ID from user profile. This is non-critical.", error);
    }
};


export const migrateAnonymousSession = async (anonymousUid, newUid, sessionId) => {
    return fetchApi("/api/sessions/migrate", {
        method: "POST",
        body: JSON.stringify({
            anonymous_uid: anonymousUid,
            new_uid: newUid,
            session_id: sessionId,
        }),
    }, true);
};

export const updateEncryptedConversation = async (sessionId, encryptedData, iv, sessionKey) => { // Add sessionKey param
    const payload = {
        encrypted_conversation: encryptedData,
        iv: iv,
    };
    if (sessionKey) payload.session_key = sessionKey; // Add to payload

    return fetchApi(`/api/sessions/${sessionId}/update_encrypted`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
};


/**
 * Fetches the user's current voice credit status from the backend.
 * The backend handles the daily credit reset logic.
 * @returns {Promise<{credits: number, seconds_per_credit: number, is_pro: boolean}>}
 */
export const getVoiceCreditStatus = async () => {
    return fetchApi("/api/users/me/voice-credits", {}, true);
};

/**
 * Tells the backend to burn a specific amount of voice credits for the current user.
 * @param {number} amount - The number of credits to burn.
 */
export const burnVoiceCredits = async (amount = 1) => {
    return fetchApi(
      "/api/users/me/voice-credits/burn",
      {
        method: "POST",
        body: JSON.stringify({ amount }),
      },
      true
    );
};
export const generateUserProfile = async (aboutYou) =>
    fetchApi('/api/users/me/generate-profile', {
        method: 'POST',
        body: JSON.stringify({ about_you: aboutYou }),
    }, true);

export const restoreVoiceSession = async (sessionId) => {
    return fetchApi(
      `/api/sessions/${sessionId}/restore`,
      {
        method: 'PATCH',
      },
      true // Ensures the Authorization header is included
    );
  };
  

  /**
 * Submits detailed user feedback from the early user dialog.
 * @param {object} feedbackData - The feedback payload.
 * @param {string} [feedbackData.name] - User's name.
 * @param {string} feedbackData.satisfaction - The string rating (e.g., 'Excellent').
 * @param {string} feedbackData.feedback - The detailed feedback text.
 * @param {string} [feedbackData.user_id] - The user's ID.
 */
export const submitEarlyFeedback = async (feedbackData) => {
    const payload = {
      ...feedbackData,
      submitted_at: new Date().toISOString(),
    };

    return fetchPublicApi('/api/submit-feedback', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
  };

/**
 * Submits case-study feedback (star rating + testimonial) captured from
 * CaseStudyFeedbackDialog. Triggers an admin email alert server-side.
 * @param {object} feedbackData - The feedback payload.
 * @param {string} [feedbackData.name] - User's name.
 * @param {number} feedbackData.rating - 1-5 star rating.
 * @param {string} feedbackData.satisfaction - The string rating (e.g., 'Excellent').
 * @param {string} feedbackData.feedback - The testimonial text.
 * @param {string} [feedbackData.user_id] - The user's ID.
 */
export const submitCaseStudyFeedback = async (feedbackData) => {
  const payload = {
    ...feedbackData,
    submitted_at: new Date().toISOString(),
  };

  return fetchPublicApi('/api/submit-case-study-feedback', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};


  export const getVoiceSessionPrompt = async () => {
    const auth = getAuth();
    const user = auth.currentUser;
  
    if (!user) {
      throw new Error('Authentication required to fetch voice prompt.');
    }
  
    const token = await user.getIdToken();
    const initResp = await fetch(`${API_BASE_URL}/api/voice-session-init`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  
    if (!initResp.ok) {
      const errorText = await initResp.text();
      throw new Error(`Failed to initialize voice session instructions: ${errorText}`);
    }
  
    const { instructions } = await initResp.json();
    return instructions;
  };
  
  // I'm adding the missing function here to handle the summary generation request
  /**
   * `includeSummary: false` skips the written summary but still posts the payload,
   * because long-term memory and symptom-graph processing are queued by this
   * endpoint — not calling it would silently drop both.
   */
  export const getVoiceSessionSummary = async (payload, includeSummary = true) => {
      return fetchApi(`/api/voice-session-summary${includeSummary ? '' : '?summary=false'}`, {
          method: 'POST',
          body: JSON.stringify(payload),
      }, true);
  };


  // --- Community Prompts API Functions ---

/**
 * Fetches community prompts from the backend.
 * @param {object} params - The query parameters.
 * @param {string} params.sort - 'hot', 'new', 'top'.
 * @param {string} [params.category] - The category to filter by.
 * @param {string} [params.author_id] - The author's user ID to filter by.
 * @returns {Promise<Array>} A promise that resolves to an array of prompts.
 */
export const getCommunityPrompts = async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return fetchPublicApi(`/api/community-prompts?${query}`);
};
  
  /**
   * Submits a new community prompt for validation and creation.
   * @param {object} promptData - The prompt data.
   * @param {string} promptData.title - The title of the prompt.
   * @param {string} promptData.description - The description of the prompt.
   * @param {string} promptData.prompt - The full prompt content.
   * @returns {Promise<object>} A promise that resolves to the newly created prompt data.
   */
  export const submitCommunityPrompt = async (promptData) => {
    return fetchApi('/api/community-prompts', {
      method: 'POST',
      body: JSON.stringify(promptData),
    }, true);
  };
  
  /**
   * Deletes a community prompt owned by the current user.
   * @param {string} promptId - The ID of the prompt to delete.
   * @returns {Promise<void>}
   */
  export const deleteCommunityPrompt = async (promptId) => {
    return fetchApi(`/api/community-prompts/${promptId}`, {
      method: 'DELETE',
    }, true);
  };
  
  /**
   * Votes on a community prompt.
   * @param {string} promptId - The ID of the prompt to vote on.
   * @param {'up' | 'down'} direction - The direction of the vote.
   * @returns {Promise<object>}
   */
  export const voteOnPrompt = async (promptId, direction) => {
    return fetchApi(`/api/community-prompts/${promptId}/vote`, {
      method: 'POST',
      body: JSON.stringify({ direction }),
    }, true);
  };


  export const incrementPromptUseCount = async (promptId) => {
    // We use fetchPublicApi because guests can also use prompts
    return fetchPublicApi(`/api/community-prompts/${promptId}/increment`, {
      method: 'POST',
    });
  };


// -- Send grid emails
  export const sendCustomVerificationEmail = async () => {
    return fetchApi('/api/auth/send-verification-email', {
      method: 'POST',
    }, true); // true = Authentication is required
  };

  export const sendPasswordResetEmail = async (email) => {
    return fetchApi('/api/auth/send-password-reset-email', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }, false); // false = Authentication is NOT required
  };


  export const updateSessionDuration = async (sessionId, secondsToAdd) => {
    if (!secondsToAdd || secondsToAdd <= 0) return;

    // Use getAuth directly to ensure we get a token
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;
    const token = await user.getIdToken();

    try {
        await fetch(`${API_BASE_URL}/api/sessions/${sessionId}/duration`, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ seconds_to_add: Math.round(secondsToAdd) }),
            keepalive: true 
        });
    } catch (e) {
        // console.warn("Failed to heartbeat duration:", e);
    }
};


export const getSessionSafetyStatus = async (sessionId) => {
    return fetchApi(`/api/sessions/${sessionId}/safety-status`, {}, true, 0); 
};

export const acknowledgeSafetyMessage = async (sessionId) => {
    return fetchApi(`/api/sessions/${sessionId}/safety-ack`, { method: "POST" }, true);
};

export const submitSafetyAgreement = async () => {
    return fetchApi('/api/users/me/safety-agreement', {
        method: 'POST'
    }, true);
};

export const checkSafetyAgreement = async () => {
    return fetchApi('/api/users/me/safety-agreement', { suppressLogging: true }, true);
};

export const validatePromoCode = async (code) => {
    // Use false for includeAuth because the user isn't registered yet
    return fetchApi('/api/promos/validate', {
        method: 'POST',
        body: JSON.stringify({ code }),
        expectedErrors: [400, 404] 
    }, false);
};

export const redeemPromoCode = async (code, userId) => {
    // Use true for includeAuth because the user is now registered and logged in
    return fetchApi('/api/promos/redeem', {
        method: 'POST',
        body: JSON.stringify({ code, user_id: userId }),
    }, true);
};

export const applyPromoCode = async (code) => {
    // Use true for includeAuth as this is for logged-in existing users
    return fetchApi('/api/promos/apply', {
        method: 'POST',
        body: JSON.stringify({ code }),
        expectedErrors: [400, 404]
    }, true);
};

export const generateSessionPDF = async (payload) => {
    return fetchApi('/api/generate-pdf', {
        method: 'POST',
        body: JSON.stringify(payload),
        responseType: 'blob' // Important: Expect a binary file
    }, true);
};


export const dismissGlobalSafetyAlert = async (action = 'dismissed') => {
    const auth = getAuth();
    const token = await auth.currentUser.getIdToken();
    
    // Pass the action as a query parameter
    const response = await fetch(`${API_BASE_URL}/api/users/me/safety-alert?action=${action}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
  
    if (!response.ok) {
      throw new Error('Failed to dismiss safety alert');
    }
    return true;
};



/**
 * consolidated function to handle Note CRUD operations via backend API.
 * @param {string} action - 'GET', 'CREATE', or 'DELETE'
 * @param {object} payload - Data required for the action
 * @returns {Promise<any>}
 */
export const handleNoteAction = async (action, payload = {}) => {
    // 1. GET ALL NOTES
    if (action === 'GET') {
        return fetchApi('/api/notes', { method: 'GET' }, true);
    }

    // 2. CREATE NOTE
    if (action === 'CREATE') {
        return fetchApi('/api/notes', {
            method: 'POST',
            body: JSON.stringify(payload)
        }, true);
    }

    // 3. UPDATE NOTE (NEW)
    if (action === 'UPDATE') {
        if (!payload.noteId) throw new Error("Note ID required for update.");
        return fetchApi(`/api/notes/${payload.noteId}`, {
            method: 'PATCH',
            body: JSON.stringify({
                title: payload.title,
                encrypted_content: payload.encrypted_content,
                iv: payload.iv,
                salt: payload.salt
            })
        }, true);
    }

    // 4. DELETE NOTE
    if (action === 'DELETE') {
        if (!payload.noteId) throw new Error("Note ID required for deletion.");
        return fetchApi(`/api/notes/${payload.noteId}`, {
            method: 'DELETE'
        }, true);
    }

    throw new Error(`Invalid action: ${action}`);
};

// Keep scanTherapyNoteRobust separate as it handles FormData (binary)
export const scanTherapyNoteRobust = async (formData) => {
    const token = await getIdToken();
    const response = await fetch(`${API_BASE_URL}/api/notes/scan`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${token}` 
        },
        body: formData
    });
    
    if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || "Scan failed");
    }
    return response.json();
}


export const getUserTools = async () => {
    return fetchApi('/api/tools', { method: 'GET' }, true);
};

export const createUserTool = async (toolData) => {
    return fetchApi('/api/tools', {
        method: 'POST',
        body: JSON.stringify(toolData)
    }, true);
};

export const updateUserTool = async (toolId, toolData) => {
    return fetchApi(`/api/tools/${toolId}`, {
        method: 'PATCH',
        body: JSON.stringify(toolData)
    }, true);
};

export const deleteUserTool = async (toolId) => {
    return fetchApi(`/api/tools/${toolId}`, {
        method: 'DELETE'
    }, true);
};


export const getBusinessStats = async () => {
    return fetchApi('/api/business/stats', {
        method: 'GET'
    }, true);
};

export const monitorVoiceSession = async (payload) => {
    // Payload: { session_id, turn_count, user_transcript }
    return fetchApi('/api/voice/monitor', {
        method: 'POST',
        body: JSON.stringify(payload),
    }, true);
};


// --- Symptom Graph (MindGraph) ---

export const getSymptomGraph = async (userId) => {
    return fetchApi(`/api/symptom-graph/${userId}`, {
        method: 'GET',
    }, true);
};

export const explainSymptomGraph = async (userId) => {
    return fetchApi(`/api/symptom-graph/${userId}/explain`, {
        method: 'POST',
    }, true);
};

export const logMindGraphView = async (userId) => {
    return fetchApi(`/api/symptom-graph/${userId}/view`, {
        method: 'POST',
    }, true);
};


// ─────────────────────────────────────────────────────────────────────────────
//  ZOSA Pathway (B2B clinical handoff)
// ─────────────────────────────────────────────────────────────────────────────

// Client-facing
export const getPathwayClientMe = async () =>
    fetchApi(`/api/pathway/client/me`, { method: "GET" }, true);

export const getPathwayReferral = async (partnerId, refCode) =>
    fetchPublicApi(`/api/pathway/referral/${encodeURIComponent(partnerId)}/${encodeURIComponent(refCode)}`);

export const claimPathwayReferral = async (partnerId, refCode) =>
    fetchApi(`/api/pathway/claim`, {
        method: "POST",
        body: JSON.stringify({ partner_id: partnerId, ref_code: refCode }),
    }, true);

export const startPathwayReport = async (partnerId = null) =>
    fetchApi(`/api/pathway/report/start`, {
        method: "POST",
        body: JSON.stringify({ partner_id: partnerId }),
    }, true);

export const getPathwayReportStatus = async (taskId) =>
    fetchApi(`/api/pathway/report/status/${encodeURIComponent(taskId)}`, { method: "GET" }, true);

export const getPathwayClientReport = async (partnerId = null) => {
    const qs = partnerId ? `?partner_id=${encodeURIComponent(partnerId)}` : "";
    return fetchApi(`/api/pathway/report${qs}`, { method: "GET" }, true);
};

export const confirmPathwayReport = async ({ partnerId = null, flaggedSections = [], clientNote = "" } = {}) =>
    fetchApi(`/api/pathway/report/confirm`, {
        method: "POST",
        body: JSON.stringify({ partner_id: partnerId, flagged_sections: flaggedSections, client_note: clientNote }),
    }, true);

// Partner dashboard
export const getPartnerMe = async () =>
    fetchApi(`/api/pathway/partner/me`, { method: "GET" }, true);

export const getPartnerClients = async () =>
    fetchApi(`/api/pathway/partner/clients`, { method: "GET" }, true);

export const mintPartnerReferral = async ({ clientLabel = "", clientInstructions = "" } = {}) =>
    fetchApi(`/api/pathway/partner/referrals`, {
        method: "POST",
        body: JSON.stringify({ client_label: clientLabel, client_instructions: clientInstructions }),
    }, true);

export const updatePartnerReferral = async (refCode, { clientInstructions, status } = {}) =>
    fetchApi(`/api/pathway/partner/referrals/${encodeURIComponent(refCode)}`, {
        method: "PATCH",
        body: JSON.stringify({ client_instructions: clientInstructions, status }),
    }, true);

export const removePartnerClient = async (refCode) =>
    fetchApi(`/api/pathway/partner/referrals/${encodeURIComponent(refCode)}`, {
        method: "DELETE",
    }, true);

export const getPartnerOrg = async () =>
    fetchApi(`/api/pathway/partner/org`, { method: "GET" }, true);

export const updatePartnerOrg = async (updates) =>
    fetchApi(`/api/pathway/partner/org`, {
        method: "PATCH",
        body: JSON.stringify(updates),
    }, true);

export const getPartnerReport = async (refCode) =>
    fetchApi(`/api/pathway/partner/report/${encodeURIComponent(refCode)}`, { method: "GET" }, true);